const SVG_NS = "http://www.w3.org/2000/svg";
const MOBILE_QUERY = "(max-width: 991px)";
const VIEWBOX_WIDTH = 1200;
const VIEWBOX_HEIGHT = 820;
const DISABLED_FLAG_VALUES = new Set(["0", "false", "off", "no"]);
const SECTION_LINES_STATE_KEY = "__sectionLinesDominantState";

function isSectionLinesEnabled(root = document) {
  const doc = root === document ? document : root?.ownerDocument || document;
  if (doc?.body?.classList?.contains("section-lines-disabled")) return false;
  const raw = doc?.body?.dataset?.sectionLinesEnabled;
  if (raw === undefined || raw === null || raw === "") return true;
  return !DISABLED_FLAG_VALUES.has(String(raw).trim().toLowerCase());
}

function toNumber(raw, fallback) {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toInt(raw, fallback) {
  return Math.round(toNumber(raw, fallback));
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function hashText(input) {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0;
}

function createRng(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randomBetween(rng, min, max) {
  return min + (max - min) * rng();
}

function randomIntBetween(rng, min, max) {
  return Math.floor(randomBetween(rng, min, max + 1));
}

function readCssNumber(style, variable, fallback) {
  const raw = style.getPropertyValue(variable);
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseRange(section, isMobile, style) {
  const cssDesktopMin = readCssNumber(style, "--ani-section-lines-count-min", 3);
  const cssDesktopMax = readCssNumber(style, "--ani-section-lines-count-max", 6);
  const desktopMin = clamp(toInt(section.dataset.bgLinesMin, cssDesktopMin), 1, 12);
  const desktopMax = clamp(toInt(section.dataset.bgLinesMax, cssDesktopMax), desktopMin, 14);
  const cssMobileMin = readCssNumber(style, "--ani-section-lines-count-mobile-min", Math.max(1, desktopMin - 1));
  const cssMobileMax = readCssNumber(style, "--ani-section-lines-count-mobile-max", Math.max(cssMobileMin, desktopMax - 2));
  const mobileMin = clamp(toInt(section.dataset.bgLinesMobileMin, cssMobileMin), 1, desktopMax);
  const mobileMax = clamp(toInt(section.dataset.bgLinesMobileMax, cssMobileMax), mobileMin, desktopMax);
  return isMobile ? { min: mobileMin, max: mobileMax } : { min: desktopMin, max: desktopMax };
}

function createSvgElement(name) {
  return document.createElementNS(SVG_NS, name);
}

function createGradient(id, style) {
  const gradient = createSvgElement("linearGradient");
  gradient.setAttribute("id", id);
  gradient.setAttribute("x1", "50%");
  gradient.setAttribute("y1", "0%");
  gradient.setAttribute("x2", "50%");
  gradient.setAttribute("y2", "100%");
  gradient.classList.add("ani-section-lines__gradient");

  const offsetVariables = [
    "--ani-section-lines-stop-edge-top-offset",
    "--ani-section-lines-stop-soft-top-offset",
    "--ani-section-lines-stop-core-top-offset",
    "--ani-section-lines-stop-peak-offset",
    "--ani-section-lines-stop-core-bottom-offset",
    "--ani-section-lines-stop-soft-bottom-offset",
    "--ani-section-lines-stop-edge-bottom-offset",
  ];
  const offsetFallbacks = [0, 12, 34, 50, 67, 88, 100];
  const offsets = [];
  let previousOffset = 0;
  for (let i = 0; i < offsetVariables.length; i += 1) {
    const parsed = readCssNumber(style, offsetVariables[i], offsetFallbacks[i]);
    const value = clamp(parsed, previousOffset, 100);
    offsets.push(`${value}%`);
    previousOffset = value;
  }

  const classes = [
    "ani-section-lines__stop-edge-top",
    "ani-section-lines__stop-soft-top",
    "ani-section-lines__stop-core-top",
    "ani-section-lines__stop-peak",
    "ani-section-lines__stop-core-bottom",
    "ani-section-lines__stop-soft-bottom",
    "ani-section-lines__stop-edge-bottom",
  ];

  for (let i = 0; i < offsets.length; i += 1) {
    const stop = createSvgElement("stop");
    stop.setAttribute("offset", offsets[i]);
    stop.classList.add(classes[i]);
    gradient.append(stop);
  }

  return gradient;
}

function curveCommands(points, tension, withMove) {
  if (!points.length) return "";
  const commands = [];
  if (withMove) {
    commands.push("M", points[0].x, points[0].y);
  }
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;
    commands.push("C", cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
  }
  return commands.join(" ");
}

function readGenerationConfig(style) {
  const tipMin = clamp(readCssNumber(style, "--ani-section-lines-tip-width-min", 0.8), 0.1, 80);
  const tipMax = clamp(readCssNumber(style, "--ani-section-lines-tip-width-max", 3.2), tipMin, 100);
  const midMin = clamp(readCssNumber(style, "--ani-section-lines-middle-width-min", 34), tipMax, 260);
  const midMax = clamp(readCssNumber(style, "--ani-section-lines-middle-width-max", 110), midMin, 320);
  const lowerMin = clamp(readCssNumber(style, "--ani-section-lines-lower-width-min", 24), tipMax, 240);
  const lowerMax = clamp(readCssNumber(style, "--ani-section-lines-lower-width-max", 82), lowerMin, 300);
  const lengthVariationMin = clamp(readCssNumber(style, "--ani-section-lines-length-variation-min", 0.75), 0.2, 3);
  const lengthVariationMax = clamp(readCssNumber(style, "--ani-section-lines-length-variation-max", 1.25), lengthVariationMin, 3.5);
  const widthScaleMin = clamp(readCssNumber(style, "--ani-section-lines-width-scale-min", 0.8), 0.2, 3);
  const widthScaleMax = clamp(readCssNumber(style, "--ani-section-lines-width-scale-max", 1.3), widthScaleMin, 4);
  const curveScaleMin = clamp(readCssNumber(style, "--ani-section-lines-curve-variation-min", 0.85), 0.3, 2.5);
  const curveScaleMax = clamp(readCssNumber(style, "--ani-section-lines-curve-variation-max", 1.2), curveScaleMin, 3);
  const centerJitterScaleMin = clamp(readCssNumber(style, "--ani-section-lines-center-jitter-variation-min", 0.75), 0.2, 3);
  const centerJitterScaleMax = clamp(readCssNumber(style, "--ani-section-lines-center-jitter-variation-max", 1.35), centerJitterScaleMin, 3);
  const midpointJitter = clamp(readCssNumber(style, "--ani-section-lines-midpoint-jitter", 0.07), 0, 0.35);
  const verticalOffsetMin = clamp(readCssNumber(style, "--ani-section-lines-vertical-offset-min", -180), -VIEWBOX_HEIGHT * 1.3, VIEWBOX_HEIGHT * 1.3);
  const verticalOffsetMax = clamp(readCssNumber(style, "--ani-section-lines-vertical-offset-max", 180), verticalOffsetMin, VIEWBOX_HEIGHT * 1.3);

  return {
    lengthFactor: clamp(readCssNumber(style, "--ani-section-lines-length-factor", 2.6), 0.25, 5),
    centerJitter: clamp(readCssNumber(style, "--ani-section-lines-center-jitter", 110), 0, VIEWBOX_WIDTH * 0.5),
    curveAmount: clamp(readCssNumber(style, "--ani-section-lines-curve-amount", 220), 0, VIEWBOX_WIDTH * 0.7),
    tension: clamp(readCssNumber(style, "--ani-section-lines-curve-tension", 1), 0.1, 2.8),
    midpointBias: clamp(readCssNumber(style, "--ani-section-lines-midpoint-bias", 0), -0.25, 0.25),
    midpointJitter,
    verticalOffsetMin,
    verticalOffsetMax,
    lengthVariationMin,
    lengthVariationMax,
    widthScaleMin,
    widthScaleMax,
    curveScaleMin,
    curveScaleMax,
    centerJitterScaleMin,
    centerJitterScaleMax,
    tipMin,
    tipMax,
    midMin,
    midMax,
    lowerMin,
    lowerMax,
  };
}

function createRibbonData(index, total, rng, config) {
  const centerBase = ((index + 1) / (total + 1)) * VIEWBOX_WIDTH;
  const lineLengthScale = randomBetween(rng, config.lengthVariationMin, config.lengthVariationMax);
  const length = clamp(VIEWBOX_HEIGHT * config.lengthFactor * lineLengthScale, VIEWBOX_HEIGHT * 0.18, VIEWBOX_HEIGHT * 4);
  const centerY = VIEWBOX_HEIGHT * 0.5 + randomBetween(rng, config.verticalOffsetMin, config.verticalOffsetMax);
  const yTop = centerY - length * 0.5;
  const yBottom = centerY + length * 0.5;
  const ySpan = yBottom - yTop;

  const curveAmount = config.curveAmount * randomBetween(rng, config.curveScaleMin, config.curveScaleMax);
  const jitterAmount = config.centerJitter * randomBetween(rng, config.centerJitterScaleMin, config.centerJitterScaleMax);
  const laneOffset = randomBetween(rng, -jitterAmount, jitterAmount);
  const baseX = centerBase + laneOffset;
  const sideMin = -VIEWBOX_WIDTH * 0.35;
  const sideMax = VIEWBOX_WIDTH * 1.35;

  const x0 = clamp(baseX + randomBetween(rng, -curveAmount * 0.12, curveAmount * 0.12), sideMin, sideMax);
  const x1 = clamp(baseX + randomBetween(rng, -curveAmount, curveAmount), sideMin, sideMax);
  const x2 = clamp(baseX + randomBetween(rng, -curveAmount, curveAmount), sideMin, sideMax);
  const x3 = clamp(baseX + randomBetween(rng, -curveAmount * 0.12, curveAmount * 0.12), sideMin, sideMax);

  const y1Base = yTop + ySpan * (0.34 + config.midpointBias * 0.5);
  const y2Base = yTop + ySpan * (0.66 + config.midpointBias * 0.5);
  const y1 = y1Base + randomBetween(rng, -ySpan * config.midpointJitter, ySpan * config.midpointJitter);
  const y2 = y2Base + randomBetween(rng, -ySpan * config.midpointJitter, ySpan * config.midpointJitter);

  const lineWidthScale = randomBetween(rng, config.widthScaleMin, config.widthScaleMax);
  const w0 = randomBetween(rng, config.tipMin, config.tipMax) * lineWidthScale;
  const w1 = randomBetween(rng, config.midMin, config.midMax) * lineWidthScale;
  const w2 = randomBetween(rng, config.lowerMin, config.lowerMax) * lineWidthScale;
  const w3 = randomBetween(rng, config.tipMin, config.tipMax) * lineWidthScale;

  const centers = [
    { x: x0, y: yTop },
    { x: x1, y: y1 },
    { x: x2, y: y2 },
    { x: x3, y: yBottom },
  ];
  const widths = [w0, w1, w2, w3];

  const left = centers.map((point, idx) => ({
    x: point.x - widths[idx] * 0.5,
    y: point.y,
  }));
  const right = centers.map((point, idx) => ({
    x: point.x + widths[idx] * 0.5,
    y: point.y,
  }));
  const rightReverse = [right[3], right[2], right[1], right[0]];

  return `${curveCommands(left, config.tension, true)} L ${right[3].x} ${right[3].y} ${curveCommands(rightReverse, config.tension, false)} Z`;
}

function isEligibleSection(section) {
  if (!section || section.tagName !== "SECTION") return false;
  if (!section.hasAttribute("data-ani-section")) return false;
  if (section.id === "section-hero" || section.classList.contains("section-hero")) return false;
  const mode = String(section.dataset.bgLines || "").toLowerCase();
  if (mode === "off" || mode === "false" || mode === "0") return false;
  return true;
}

function ensureForegroundLayering(section, layer) {
  const children = Array.from(section.children);
  for (const child of children) {
    if (child === layer) continue;
    if (window.getComputedStyle(child).position === "static") {
      child.classList.add("ani-section-lines-foreground");
    }
  }
}

function buildSectionLines(section, sectionIndex, isMobile) {
  if (section.dataset.bgLinesReady === "true") return;
  const existing = section.querySelector(":scope > [data-bg-lines-layer]");
  if (existing) {
    section.dataset.bgLinesReady = "true";
    return;
  }

  const seedSource = `${section.id || "section"}:${section.className}:${sectionIndex}`;
  const seed = hashText(seedSource);
  const rng = createRng(seed);

  section.classList.add("ani-section-lines-host");
  if (window.getComputedStyle(section).position === "static") {
    section.classList.add("ani-section-lines-host-relative");
  }

  const style = window.getComputedStyle(section);
  const generation = readGenerationConfig(style);

  const range = parseRange(section, isMobile, style);
  const lineCount = randomIntBetween(rng, range.min, range.max);
  const gradientId = `bg-lines-grad-${sectionIndex}-${seed.toString(16)}`;

  const layer = document.createElement("div");
  layer.className = "ani-section-lines";
  layer.setAttribute("aria-hidden", "true");
  layer.setAttribute("data-bg-lines-layer", "");
  if (section.dataset.bgLinesOpacity) {
    layer.style.setProperty("--ani-section-lines-opacity", String(toNumber(section.dataset.bgLinesOpacity, 1)));
  }
  if (section.dataset.bgLinesBlur) {
    layer.style.setProperty("--ani-section-lines-blur", `${toNumber(section.dataset.bgLinesBlur, 0.4)}px`);
  }
  if (section.dataset.bgLinesDominantOpacity) {
    layer.style.setProperty("--ani-section-lines-opacity-dominant", String(toNumber(section.dataset.bgLinesDominantOpacity, 1)));
  }
  if (section.dataset.bgLinesDominantBlur) {
    layer.style.setProperty("--ani-section-lines-blur-dominant", `${toNumber(section.dataset.bgLinesDominantBlur, 0.2)}px`);
  }

  const svg = createSvgElement("svg");
  svg.classList.add("ani-section-lines__svg");
  svg.setAttribute("viewBox", `0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`);
  svg.setAttribute("preserveAspectRatio", "none");
  svg.setAttribute("focusable", "false");
  svg.setAttribute("aria-hidden", "true");

  const defs = createSvgElement("defs");
  defs.append(createGradient(gradientId, style));
  svg.append(defs);

  for (let i = 0; i < lineCount; i += 1) {
    const path = createSvgElement("path");
    path.classList.add("ani-section-lines__path");
    path.setAttribute("d", createRibbonData(i, lineCount, rng, generation));
    path.setAttribute("fill", `url(#${gradientId})`);
    path.style.setProperty("--ani-section-lines-drift-distance", `${randomBetween(rng, 10, 36).toFixed(2)}px`);
    path.style.setProperty("--ani-section-lines-drift-duration", `${randomBetween(rng, 30, 58).toFixed(2)}s`);
    path.style.setProperty("--ani-section-lines-delay", `${randomBetween(rng, -30, -1).toFixed(2)}s`);
    path.style.setProperty("--ani-section-lines-pulse-duration", `${randomBetween(rng, 13, 28).toFixed(2)}s`);
    path.style.setProperty("--ani-section-lines-pulse-min", `${randomBetween(rng, 0.68, 0.9).toFixed(3)}`);
    svg.append(path);
  }

  layer.append(svg);

  section.insertBefore(layer, section.firstChild);
  ensureForegroundLayering(section, layer);
  section.dataset.bgLinesReady = "true";
}

function gatherSections(root = document) {
  const sections = Array.from(root.querySelectorAll("section[data-ani-section]"));
  return sections.filter((section) => isEligibleSection(section));
}

function removeSectionLines(section) {
  if (!section || !section.querySelector) return;
  const layer = section.querySelector(":scope > [data-bg-lines-layer]");
  if (layer) layer.remove();
  section.dataset.bgLinesReady = "false";
}

function getVisibleRatio(section) {
  if (!section) return 0;
  const rect = section.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight || 1;
  const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
  return visible / vh;
}

function resolveInitialDominantSection(sections) {
  if (!sections.length) return null;
  const dominant = sections.find((section) => section.classList.contains("is-dominant"));
  if (dominant) return dominant;
  let best = sections[0];
  let bestRatio = getVisibleRatio(best);
  for (let i = 1; i < sections.length; i += 1) {
    const ratio = getVisibleRatio(sections[i]);
    if (ratio > bestRatio) {
      best = sections[i];
      bestRatio = ratio;
    }
  }
  return best;
}

export function initSectionLinesBackground(root = document) {
  const previous = globalThis[SECTION_LINES_STATE_KEY];
  if (!isSectionLinesEnabled(root)) {
    if (previous?.bound && typeof previous.destroy === "function") {
      previous.destroy();
    }
    globalThis[SECTION_LINES_STATE_KEY] = { bound: false, root, destroy: null };
    return;
  }
  const sections = gatherSections(root);
  if (!sections.length) return;
  if (previous?.root === root && previous?.bound) return;
  if (previous?.bound && typeof previous.destroy === "function") {
    previous.destroy();
  }

  const sectionIndexMap = new Map();
  for (let i = 0; i < sections.length; i += 1) {
    sectionIndexMap.set(sections[i], i);
    removeSectionLines(sections[i]);
  }

  let activeSection = null;
  const getIsMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  const activateSection = (section) => {
    if (!section || !sectionIndexMap.has(section)) return;
    if (activeSection === section) return;
    if (activeSection) removeSectionLines(activeSection);
    buildSectionLines(section, sectionIndexMap.get(section), getIsMobile());
    activeSection = section;
  };

  const onDominantChange = (event) => {
    activateSection(event?.detail?.section || null);
  };

  document.addEventListener("ani:dominant-change", onDominantChange, true);
  requestAnimationFrame(() => {
    activateSection(resolveInitialDominantSection(sections));
  });

  const destroy = () => {
    document.removeEventListener("ani:dominant-change", onDominantChange, true);
    if (activeSection) removeSectionLines(activeSection);
    activeSection = null;
  };

  globalThis[SECTION_LINES_STATE_KEY] = { bound: true, root, destroy };
}
